// src/hooks/useConnectivityState.js
// Unified connectivity state — three independent layers.
//
// networkState:  ONLINE | DEGRADED | OFFLINE  — device→internet (XHR probe)
// backendState:  CONNECTED | DEGRADED | UNAVAILABLE — Firestore reachability
// sourceState:   LIVE | STALE | CACHED | UNAVAILABLE — data freshness
//
// Prevents contradictory UI: "CONNECTED" + "Showing cached data"
// by making each layer explicit and independently observable.

import { useState, useEffect, useRef } from 'react';
import { useSituationStore, selectFreshness, selectSourceHealth } from '@store/situationStore';

export const NETWORK = { ONLINE: 'ONLINE', DEGRADED: 'DEGRADED', OFFLINE: 'OFFLINE' };
export const BACKEND = { CONNECTED: 'CONNECTED', DEGRADED: 'DEGRADED', UNAVAILABLE: 'UNAVAILABLE' };
export const SOURCE  = { LIVE: 'LIVE', STALE: 'STALE', CACHED: 'CACHED', UNAVAILABLE: 'UNAVAILABLE' };

export function useConnectivityState() {
  const [networkState, setNetworkState] = useState(NETWORK.ONLINE);
  const [backendState, setBackendState] = useState(BACKEND.CONNECTED);
  const failCount  = useRef(0);
  const cancelled  = useRef(false);

  // Network layer — XHR probe
  useEffect(() => {
    cancelled.current = false;
    async function probe() {
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('HEAD', window.location.origin + '/favicon.ico?nc=' + Date.now(), true);
          xhr.timeout = 5000;
          xhr.onload    = () => resolve(true);
          xhr.onerror   = () => reject();
          xhr.ontimeout = () => reject();
          xhr.send();
        });
        if (cancelled.current) return;
        failCount.current = 0;
        setNetworkState(NETWORK.ONLINE);
      } catch {
        if (cancelled.current) return;
        failCount.current++;
        setNetworkState(failCount.current >= 2 ? NETWORK.OFFLINE : NETWORK.DEGRADED);
      }
    }
    const on  = () => { failCount.current = 0; probe(); };
    const off = () => setNetworkState(NETWORK.OFFLINE);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    const id = setInterval(probe, 60_000);
    probe();
    return () => {
      cancelled.current = true;
      clearInterval(id);
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Backend layer — Firestore sentinel probe
  useEffect(() => {
    let unsub;
    (async () => {
      try {
        const { getDB } = await import('@services/storage/firebase');
        const { db } = await getDB();
        if (!db) { setBackendState(BACKEND.UNAVAILABLE); return; }
        const { doc, onSnapshot } = await import('firebase/firestore');
        unsub = onSnapshot(
          doc(db, 'public_snapshots', 'trend_indicators'), // existing doc — backend reachability probe
          () => setBackendState(BACKEND.CONNECTED),
          () => setBackendState(BACKEND.UNAVAILABLE)
        );
      } catch {
        setBackendState(BACKEND.UNAVAILABLE);
      }
    })();
    return () => unsub?.();
  }, []);

  // Source layer — data freshness from situationStore
  const freshness  = useSituationStore(selectFreshness);
  const nwsHealth  = useSituationStore(selectSourceHealth('nws'));
  const dotdHealth = useSituationStore(selectSourceHealth('dotd'));
  const entHealth  = useSituationStore(selectSourceHealth('entergy'));

  const sourceState = (() => {
    const statuses = [nwsHealth?.status, dotdHealth?.status, entHealth?.status];
    if (statuses.every(s => s === 'live'))        return SOURCE.LIVE;
    if (statuses.every(s => s === 'unavailable')) return SOURCE.UNAVAILABLE;
    if (statuses.some(s => s === 'stale'))        return SOURCE.STALE;
    if (statuses.some(s => s === 'live'))         return SOURCE.LIVE;
    return SOURCE.CACHED;
  })();

  const isFullyOnline    = networkState === NETWORK.ONLINE &&
                           backendState === BACKEND.CONNECTED &&
                           sourceState  === SOURCE.LIVE;
  const showCachedBanner = networkState === NETWORK.OFFLINE ||
                           backendState === BACKEND.UNAVAILABLE;

  return {
    networkState,
    backendState,
    sourceState,
    freshness,
    isFullyOnline,
    showCachedBanner,
    online: networkState !== NETWORK.OFFLINE, // legacy compat
  };
}
